var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { Box, Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MoreActionIcon from '../../../components/icons/MoreActionIcon';
import { FundingLogHistoryIcon } from './FundingLogStatusHistoryPopup';
import { baseColors } from '../../../Themes/colors';
import { isFundingLogRowLocked } from './useFundingLog';
import { FundingLogStatusPill, FundingLogActionsDefault, FundingLogActionsOnHover, } from './FundingLogStyles';
var ASSIGNEE_TIER_DOT_COLOR = {
    L1: baseColors.successPreset[30],
    L2: '#F5A623',
    SALES_SUPPORT: '#6C3EC9',
};
export var ASSIGNEE_TIER_LABEL = {
    L1: 'Initial Reviewer',
    L2: 'Final Reviewer',
    SALES_SUPPORT: 'Sales Support',
};
var AssigneeDot = function (_a) {
    var color = _a.color;
    return (React.createElement(Box, { component: "span", sx: { width: 8, height: 8, borderRadius: '50%', backgroundColor: color, display: 'inline-block', flexShrink: 0 } }));
};
export var FundingLogAssigneeDropdown = function (_a) {
    var assigneeName = _a.assigneeName, status = _a.status, _b = _a.isAssignedToSelf, isAssignedToSelf = _b === void 0 ? false : _b, selfInitials = _a.selfInitials, tier = _a.tier, currentUserRoleTier = _a.currentUserRoleTier, _c = _a.currentUserIsReviewer, currentUserIsReviewer = _c === void 0 ? true : _c, onAssignToMyself = _a.onAssignToMyself, onAssignToL2 = _a.onAssignToL2, onAssignToSalesSupport = _a.onAssignToSalesSupport, onReassignToOther = _a.onReassignToOther;
    var _d = React.useState(null), anchorEl = _d[0], setAnchorEl = _d[1];
    var open = Boolean(anchorEl);
    var handleSelect = function (action) {
        setAnchorEl(null);
        action === null || action === void 0 ? void 0 : action();
    };
    var hasAssignment = Boolean(assigneeName) || Boolean(tier);
    var effectiveTier = tier !== null && tier !== void 0 ? tier : 'L1';
    var isLocked = isFundingLogRowLocked(status);
    var canClaimSecondReviewAsSelf = tier === 'L2' && !assigneeName && currentUserRoleTier === 'L2';
    var canReassignFromL1AsL2 = tier === 'L1' && currentUserRoleTier === 'L2';
    var canManage = !isLocked && (!hasAssignment || isAssignedToSelf || canClaimSecondReviewAsSelf || canReassignFromL1AsL2);
    var canReassignToOtherAsL2 = !isLocked && hasAssignment && currentUserRoleTier === 'L2' && !canClaimSecondReviewAsSelf && tier !== 'SALES_SUPPORT';
    var canOpenMenu = currentUserIsReviewer && (canManage || canReassignToOtherAsL2);
    var showsAsSelf = isAssignedToSelf && tier !== 'L1' && tier !== 'L2';
    var isUnclaimedFinalReview = tier === 'L2' && !assigneeName;
    var closedPrimaryLabel = !hasAssignment || isUnclaimedFinalReview
        ? 'Unassigned'
        : showsAsSelf
            ? "You".concat(selfInitials ? " (".concat(selfInitials, ")") : '')
            : assigneeName || ASSIGNEE_TIER_LABEL[effectiveTier];
    var closedTierLabel = isUnclaimedFinalReview
        ? 'Final Review'
        : tier && hasAssignment && (showsAsSelf || assigneeName)
            ? ASSIGNEE_TIER_LABEL[tier]
            : undefined;
    var closedFullLabel = closedTierLabel ? "".concat(closedPrimaryLabel, " (").concat(closedTierLabel, ")") : closedPrimaryLabel;
    return (React.createElement(React.Fragment, null,
        React.createElement(Tooltip, { title: closedFullLabel, placement: "top", arrow: true },
            React.createElement(Box, { component: "button", type: "button", onClick: canOpenMenu ? function (event) { return setAnchorEl(event.currentTarget); } : undefined, sx: __assign({ display: 'inline-flex', alignItems: 'center', gap: 0.75, padding: '5px 10px', borderRadius: '6px', border: "1px solid ".concat(baseColors.greyPreset[40]), backgroundColor: baseColors.fullWhite, cursor: canOpenMenu ? 'pointer' : 'default', fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 500, color: baseColors.darkPreset[10], width: '140px', justifyContent: 'space-between', maxWidth: '100%', textAlign: 'left', transition: 'border-color 0.12s' }, (canOpenMenu && { '&:hover, &:focus-visible': { borderColor: baseColors.successPreset[30] } })) },
                React.createElement(Stack, { sx: { minWidth: 0, overflow: 'hidden' } },
                    React.createElement(Box, { component: "span", sx: __assign({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, (closedPrimaryLabel === 'Unassigned' && { fontStyle: 'italic', paddingRight: '3px' })) }, closedPrimaryLabel),
                    closedTierLabel && (React.createElement(Box, { component: "span", sx: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 10,
                            fontWeight: 500,
                            color: baseColors.greyPreset[30],
                            lineHeight: 1.3,
                        } }, "(".concat(closedTierLabel, ")")))),
                canOpenMenu && React.createElement(ArrowDropDownIcon, { sx: { fontSize: 18, color: baseColors.greyPreset[20] } }))),
        canOpenMenu && (React.createElement(Menu, { anchorEl: anchorEl, open: open, onClose: function () { return setAnchorEl(null); } },
            React.createElement(Typography, { variant: "overline", sx: { px: 2, pt: 1, pb: 0.5, display: 'block', fontSize: 10, fontWeight: 700, color: baseColors.greyPreset[30] } }, "Assignee Options"),
            canManage && (React.createElement(React.Fragment, null,
                (!hasAssignment || canClaimSecondReviewAsSelf) && (React.createElement(MenuItem, { onClick: function () { return handleSelect(onAssignToMyself); }, sx: { gap: 1, fontSize: 13 } },
                    React.createElement(AssigneeDot, { color: ASSIGNEE_TIER_DOT_COLOR.L1 }),
                    "Assign to Myself")),
                (tier === 'L1' ? currentUserRoleTier !== 'L2' : (!tier && currentUserRoleTier === 'L1')) && (React.createElement(MenuItem, { onClick: function () { return handleSelect(onAssignToL2); }, sx: { gap: 1, fontSize: 13 } },
                    React.createElement(AssigneeDot, { color: ASSIGNEE_TIER_DOT_COLOR.L2 }),
                    tier === 'L1' ? 'Reassign for Final Reviewer' : 'Assign to Final Review')),
                (tier === 'L1' || (tier === 'L2' && !canClaimSecondReviewAsSelf)) && (React.createElement(MenuItem, { onClick: function () { return handleSelect(onAssignToSalesSupport); }, sx: { gap: 1, fontSize: 13 } },
                    React.createElement(AssigneeDot, { color: ASSIGNEE_TIER_DOT_COLOR.SALES_SUPPORT }),
                    "Return to Sales Support")))),
            canReassignToOtherAsL2 && (React.createElement(React.Fragment, null,
                React.createElement(Divider, { sx: { my: 0.5 } }),
                React.createElement(MenuItem, { onClick: function () { return handleSelect(onReassignToOther); }, sx: { gap: 1, fontSize: 13 } },
                    React.createElement(PeopleOutlineIcon, { sx: { fontSize: 18, color: baseColors.greyPreset[30] } }),
                    "Transfer to Another CM")))))));
};
var getFundingLogStatusColors = function (status) {
    var normalized = (status || '').toLowerCase();
    if (normalized.includes('review') || normalized.includes('docs')) {
        return { bg: '#EDE7FB', color: '#6C3EC9' };
    }
    if (normalized.includes('passed to book')) {
        return { bg: '#E6EAF0', color: '#47566B' };
    }
    if (normalized.includes('return')) {
        return { bg: '#FCEEDD', color: '#B26A00' };
    }
    if (normalized.includes('fund')) {
        return { bg: '#DFF3E1', color: '#2E7D32' };
    }
    if (normalized.includes('cancel')) {
        return { bg: '#FFE0DB', color: '#C92000' };
    }
    return { bg: baseColors.bluePreset[60], color: baseColors.bluePreset[50] };
};
export var FundingLogStatusBadge = function (_a) {
    var value = _a.value;
    var _b = getFundingLogStatusColors(value), bg = _b.bg, color = _b.color;
    return (React.createElement(FundingLogStatusPill, { sx: { backgroundColor: bg, color: color } }, (value === null || value === void 0 ? void 0 : value.trim()) ? value : '--'));
};
export var FundingLogStatusCell = function (_a) {
    var value = _a.value, onHistoryClick = _a.onHistoryClick;
    return (React.createElement(Stack, { direction: "row", alignItems: "center", gap: 0.75, sx: { minWidth: 0, width: '100%' } },
        React.createElement(Tooltip, { title: (value === null || value === void 0 ? void 0 : value.trim()) || '', arrow: true, placement: "top", disableHoverListener: !(value === null || value === void 0 ? void 0 : value.trim()) },
            React.createElement(Box, { sx: { minWidth: 0, overflow: 'hidden' } },
                React.createElement(FundingLogStatusBadge, { value: value }))),
        React.createElement(Tooltip, { title: "Contract Status History", arrow: true, placement: "top" },
            React.createElement(Box, { component: "span", role: "button", tabIndex: 0, onClick: onHistoryClick, onKeyDown: function (event) {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onHistoryClick === null || onHistoryClick === void 0 ? void 0 : onHistoryClick();
                    }
                }, sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { opacity: 0.85 },
                } },
                React.createElement(FundingLogHistoryIcon, null)))));
};
var ACTION_ICON_BUTTON_SX = {
    width: 28,
    height: 28,
    padding: 0,
};
export var FundingLogRowActionsMenu = function (_a) {
    var onView = _a.onView, onComment = _a.onComment, _b = _a.showComment, showComment = _b === void 0 ? true : _b;
    return (React.createElement(React.Fragment, null,
        React.createElement(FundingLogActionsDefault, { className: "funding-log-actions-default" },
            React.createElement(IconButton, { size: "small", sx: ACTION_ICON_BUTTON_SX },
                React.createElement(MoreActionIcon, null))),
        React.createElement(FundingLogActionsOnHover, { className: "funding-log-actions-hover" },
            React.createElement(Tooltip, { title: "View", placement: "top", arrow: true },
                React.createElement(IconButton, { size: "small", onClick: onView, sx: ACTION_ICON_BUTTON_SX },
                    React.createElement(VisibilityOutlinedIcon, { fontSize: "small" }))),
            showComment && (React.createElement(Tooltip, { title: "Comment", placement: "top", arrow: true },
                React.createElement(IconButton, { size: "small", onClick: onComment, sx: ACTION_ICON_BUTTON_SX },
                    React.createElement(ChatBubbleOutlineOutlinedIcon, { fontSize: "small" })))))));
};
